import { apiSlice } from './apiSlice';

/**
 * F-06 — Approval notifications inbox.
 *
 * Backend contract (ApprovalNotificationController):
 *   GET  /approvals/notifications                → { notifications: [{id, data, read_at, created_at}], unread_count }
 *   POST /approvals/notifications/{id}/read      → mark one as read
 *   POST /approvals/notifications/mark-all-read  → mark all as read
 *
 * Responses are deep-camelized by the axios interceptor, so the notification
 * payload surfaces as { id, data: { approvalType, sourceId, title, body,
 * deepLink }, readAt, createdAt }.
 */
export interface ApprovalNotificationData {
  approvalType?: string;
  sourceId?: number | string;
  title?: string;
  body?: string;
  deepLink?: string;
}

export interface ApprovalNotification {
  id: string;
  data: ApprovalNotificationData;
  readAt: string | null;
  createdAt: string;
}

export interface ApprovalNotificationsPayload {
  notifications: ApprovalNotification[];
  unreadCount: number;
}

/** Envelope returned by the backend's sendResponse wrapper. */
export interface ApprovalNotificationsResponse {
  data: ApprovalNotificationsPayload;
}

/**
 * `markRead` returns `sendResponse(['id' => $id], …)`, so the resolved value is
 * the FULL envelope — `result.data.id` is unsound, the id sits at
 * `result.data.data.id`.
 */
export interface ApprovalMarkReadResponse {
  data: { id: string } | null;
}

/** `markAllRead` returns `sendResponse(null, …)` — an envelope with no data. */
export interface ApprovalMarkAllReadResponse {
  data: null;
}

export const approvalsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getApprovalNotifications: builder.query<ApprovalNotificationsResponse, { unreadOnly?: boolean } | void>({
      query: (params) => ({
        url: '/approvals/notifications',
        // The api request interceptor snake_cases `params` (services/api.ts),
        // so `unreadOnly` reaches the controller as `unread_only` — send the
        // camelCase arg, never pre-snake_case it.
        params: params || undefined,
      }),
      providesTags: ['ApprovalNotification'],
    }),
    markApprovalNotificationRead: builder.mutation<ApprovalMarkReadResponse, string>({
      query: (id) => ({
        url: `/approvals/notifications/${id}/read`,
        method: 'POST',
      }),
      invalidatesTags: ['ApprovalNotification'],
    }),
    markAllApprovalNotificationsRead: builder.mutation<ApprovalMarkAllReadResponse, void>({
      query: () => ({
        url: '/approvals/notifications/mark-all-read',
        method: 'POST',
      }),
      invalidatesTags: ['ApprovalNotification'],
    }),
  }),
});

export const {
  useGetApprovalNotificationsQuery,
  useMarkApprovalNotificationReadMutation,
  useMarkAllApprovalNotificationsReadMutation,
} = approvalsApi;
